import { UserDto } from '@act/shared/data-transfer-objects';
import { IconLibrary } from '@act/storybook';
import { Avatar, Box } from '@mui/material';
import React from 'react';

export const checkBirthday = (dateOfBirth: string) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();
  const birthMonth = birthDate.getMonth();
  const birthDay = birthDate.getDate();

  if (currentMonth === birthMonth) {
    const dayDiff = Math.abs(currentDay - birthDay);
    return dayDiff <= 7 ? '🎂' : '';
  }

  // Handle month boundaries
  const daysInMonth = (month: number) => {
    const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonths[month];
  };

  // Check if months are adjacent
  const isAdjacent =
    currentMonth === birthMonth + 1 ||
    (currentMonth === 0 && birthMonth === 11) ||
    currentMonth === birthMonth - 1 ||
    (currentMonth === 11 && birthMonth === 0);

  if (isAdjacent) {
    let dayDiff;

    if (
      currentMonth === birthMonth + 1 ||
      (currentMonth === 0 && birthMonth === 11)
    ) {
      // Current month is after birth month
      const daysInBirthMonth = daysInMonth(birthMonth);
      dayDiff = daysInBirthMonth - birthDay + currentDay;
    } else {
      // Current month is before birth month
      const daysInCurrentMonth = daysInMonth(currentMonth);
      dayDiff = daysInCurrentMonth - currentDay + birthDay;
    }

    return dayDiff <= 7 ? '🎂' : '';
  }

  return '';
};

export const capitalizeFirstLetter = (name: string, onlyInitial?: boolean) => {
  if (!name) return '';

  const initial = name?.slice(0, 1)?.toLocaleUpperCase();
  return onlyInitial ? `${initial}.` : initial + name?.slice(1);
};

export const displayName = (
  member: UserDto,
  options?: {
    hideLastName?: boolean;
    hideBirthday?: boolean;
    onlyFirstInitial?: boolean;
    onlyLastInitial?: boolean;
  }
): React.ReactNode => {
  if (!member) return 'Unknown';

  // Format first and last names with optional initial formatting
  const firstName = capitalizeFirstLetter(
    member?.firstName,
    options?.onlyFirstInitial
  );
  const lastName = capitalizeFirstLetter(
    member?.lastName,
    options?.onlyLastInitial
  );
  const space = firstName && lastName ? ' ' : '';

  const name =
    !options?.hideLastName && member.lastName
      ? `${firstName || ''}${space}${lastName || ''}`
      : firstName;

  // Check for memberProgressed icon
  const memberProgressed = member?.memberProgressed?.memberProgressed;
  const memberProgressedIcon = memberProgressed ? (
    <Avatar
      className={'primary'}
      sx={{
        marginRight: 1,
        width: 'fit-content',
        height: 'fit-content',
        padding: 0.5,
      }}
    >
      <IconLibrary
        iconName="Star"
        size={12}
        weight={'fill'}
        useContrastColor={true}
      />
    </Avatar>
  ) : null;

  // Check for birthday
  const birthday =
    !options?.hideBirthday && member.dateOfBirth
      ? checkBirthday(member.dateOfBirth)
      : '';

  // If we have an icon or birthday, wrap in Box; otherwise return plain name
  if (memberProgressedIcon || birthday) {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
        }}
      >
        {memberProgressedIcon}
        {name}
        {birthday && ` ${birthday}`}
      </Box>
    );
  }

  return name;
};

export const pronounsFormatter = (pronouns: string) => {
  if (!pronouns) return '';
  // Trim pronoun formatting from they/them/theirs -> they/them
  return pronouns
    .split('/')
    .map(p => p.trim())
    .slice(0, 2)
    .join('/');
};
